import { useEffect, useRef, useState } from 'react'

// Animated number: counts up from 0 to `value` on mount (and re-runs whenever
// the value changes, so async-loaded numbers still roll in). `format` receives
// the in-flight value so callers control rounding/units.
//
// Honors prefers-reduced-motion by jumping straight to the final number.
const REDUCED = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export default function Num({
  value = 0,
  format = (n) => Math.round(n).toLocaleString(),
  duration = 900,
  className,
}) {
  const target = Number(value) || 0
  const [shown, setShown] = useState(REDUCED() ? target : 0)
  const raf = useRef(0)

  useEffect(() => {
    if (REDUCED() || !target) {
      setShown(target)
      return undefined
    }
    const start = performance.now()
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration)
      // easeOutCubic — quick out of the gate, settles gently on the number.
      setShown(target * (1 - (1 - t) ** 3))
      if (t < 1) raf.current = requestAnimationFrame(tick)
      else setShown(target)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [target, duration])

  return <span className={className}>{format(shown)}</span>
}
